import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Dispute, DisputeResolutionAction, DisputeSubStatus } from "@/types/database";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { notifyManagersDisputeFiled, notifyHostDisputeResolved } from "@/lib/notifications";
import { logActivity } from "@/lib/activityLogger";

export function useDisputes() {
  const { activeOrgId } = useAuth();

  return useQuery({
    queryKey: ["disputes", activeOrgId],
    enabled: !!activeOrgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("disputes")
        .select(`
          *,
          unit:units!disputes_unit_id_fkey(*),
          host:hosts!disputes_host_id_fkey(
            *,
            profile:profiles!hosts_user_id_fkey(*)
          ),
          booking:bookings(id, guest_name, check_in, check_out)
        `)
        .eq("organization_id", activeOrgId!)
        .order("filed_at", { ascending: false });
      if (error) throw error;
      return data as Dispute[];
    },
  });
}

export function useCreateDispute() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user, activeOrgId } = useAuth();

  return useMutation({
    mutationFn: async (dispute: {
      booking_id?: string;
      unit_id: string;
      host_id: string;
      reason: string;
      dispute_type?: string;
      priority?: string;
      complainant_type?: string;
      complainant_name?: string;
      complainant_unit?: string;
      incident_date?: string;
      amount_claimed?: number;
    }) => {
      if (!activeOrgId) throw new Error("No active organization");

      const { data, error } = await supabase
        .from("disputes")
        .insert({
          ...dispute,
          organization_id: activeOrgId,
          priority: dispute.priority ?? "medium",
          sub_status: "open",
        })
        .select()
        .single();
      if (error) throw error;

      await supabase.from("audit_logs").insert({
        organization_id: activeOrgId,
        action: "Dispute filed",
        entity: `Dispute ${data.id}`,
        detail: `Type: ${dispute.dispute_type ?? "general"} | Priority: ${dispute.priority ?? "medium"} | ${dispute.reason}`,
        user_name: user?.fullName ?? "Host",
      });

      // Notify org managers about the new dispute (fire-and-forget)
      // Fetch unit number and host name for the notification message
      const { data: hostData } = await supabase
        .from("hosts")
        .select("profile:profiles!hosts_user_id_fkey(full_name), unit:units!hosts_unit_id_fkey(unit_number)")
        .eq("id", dispute.host_id)
        .single();
      const unitNumber = (hostData?.unit as any)?.unit_number ?? "—";
      const hostName = (hostData?.profile as any)?.full_name ?? user?.fullName ?? "A host";
      notifyManagersDisputeFiled(activeOrgId, data.id, hostName, unitNumber, dispute.booking_id ?? null)
        .catch((e) => console.warn("[notify] Dispute managers notification failed:", e));

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["disputes", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["audit_logs", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["activity_feed", activeOrgId] });
      toast({ title: "Dispute filed", description: "Your dispute has been submitted for review." });
      if (activeOrgId) {
        logActivity({
          orgId: activeOrgId, userId: user?.id, actionType: "dispute_filed",
          entityType: "dispute", entityId: data.id,
          metadata: { dispute_type: data.dispute_type, priority: data.priority },
        });
      }
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}

export function useResolveDispute() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user, activeOrgId } = useAuth();

  return useMutation({
    mutationFn: async ({
      id,
      status,
      resolution,
      sub_status,
      assigned_to,
      resolution_action,
      fine_amount,
      communication_log,
    }: {
      id: string;
      status: "approved" | "rejected";
      resolution?: string;
      sub_status?: DisputeSubStatus;
      assigned_to?: string;
      resolution_action?: DisputeResolutionAction;
      fine_amount?: number;
      communication_log?: string;
    }) => {
      const updates: Record<string, any> = {
        status,
        resolution,
        resolved_at: new Date().toISOString(),
        sub_status: sub_status ?? "closed",
      };
      if (assigned_to !== undefined) updates.assigned_to = assigned_to;
      if (resolution_action !== undefined) updates.resolution_action = resolution_action;
      if (fine_amount !== undefined) updates.fine_amount = fine_amount;
      if (communication_log !== undefined) updates.communication_log = communication_log;

      // Fetch host user_id before updating (needed for notification)
      const { data: disputeRecord } = await supabase
        .from("disputes")
        .select("host_id, host:hosts!disputes_host_id_fkey(user_id)")
        .eq("id", id)
        .single();

      const { data, error } = await supabase
        .from("disputes")
        .update(updates)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;

      await supabase.from("audit_logs").insert({
        organization_id: activeOrgId,
        action: `Dispute ${status}`,
        entity: `Dispute ${id}`,
        detail: [
          resolution_action ? `Action: ${resolution_action}` : null,
          fine_amount ? `Fine: $${fine_amount}/day` : null,
          resolution || `Dispute ${status} by ${user?.fullName ?? "Admin"}`,
        ].filter(Boolean).join(" | "),
        user_name: user?.fullName ?? "Admin",
      });

      // Notify the host that their dispute has been resolved (fire-and-forget)
      const hostUserId = (disputeRecord?.host as any)?.user_id;
      if (hostUserId && activeOrgId) {
        notifyHostDisputeResolved(hostUserId, activeOrgId, id, resolution_action ?? null, resolution ?? null)
          .catch((e) => console.warn("[notify] Dispute resolved notification failed:", e));
      }

      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["disputes", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["audit_logs", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["activity_feed", activeOrgId] });
      toast({ title: `Dispute ${variables.status}`, description: `The dispute has been ${variables.status}.` });
      if (activeOrgId) {
        logActivity({
          orgId: activeOrgId, userId: user?.id, actionType: "dispute_resolved",
          entityType: "dispute", entityId: variables.id,
          metadata: { resolution_action: variables.resolution_action },
        });
      }
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}

export function useUpdateDisputeStatus() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user, activeOrgId } = useAuth();

  return useMutation({
    mutationFn: async ({
      id,
      sub_status,
      assigned_to,
      communication_log,
    }: {
      id: string;
      sub_status?: DisputeSubStatus;
      assigned_to?: string;
      communication_log?: string;
    }) => {
      const updates: Record<string, any> = {};
      if (sub_status !== undefined) updates.sub_status = sub_status;
      if (assigned_to !== undefined) updates.assigned_to = assigned_to;
      if (communication_log !== undefined) updates.communication_log = communication_log;

      const { data, error } = await supabase
        .from("disputes")
        .update(updates)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;

      await supabase.from("audit_logs").insert({
        organization_id: activeOrgId,
        action: "Dispute updated",
        entity: `Dispute ${id}`,
        detail: `Sub-status: ${sub_status ?? "unchanged"} | ${communication_log ?? ""}`,
        user_name: user?.fullName ?? "Admin",
      });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["disputes", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["audit_logs", activeOrgId] });
      toast({ title: "Dispute updated", description: "The dispute record has been updated." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}
